import { Box, Paper, Typography } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"

import { SalesOpportunityStatusChip } from "../SalesOpportunityStatusChip"

import DisplayAS400Id from "@/components/common/DisplayAS400Id/DisplayAS400Id"
import { StatusIndicator } from "@/components/common/StatusIndicator/StatusIndicator"
import DateChip from "@/components/DateChip"
import { ComputedProductStatus, type SalesPipeline } from "@/graphql/codegen/graphql"
import { ComputedProductStatusMap } from "@/screens/Companies/products/utils/productStatusMap"

interface SalesOpportunityCardProps {
  salesOpportunity: SalesPipeline
}

export const SalesOpportunityCard: FC<SalesOpportunityCardProps> = ({ salesOpportunity }) => {
  const { id, company, product, status, quoteDate } = salesOpportunity

  return (
    <Paper
      variant='outlined'
      className='flex flex-col gap-3 rounded-lg border border-gray-400 bg-white p-4'
      data-testid={`sales-opportunity.card.root-${id}`}
    >
      <Box className='flex items-start justify-between gap-x-3'>
        <Typography
          className='text-base font-medium leading-tight text-gray-900'
          data-testid={`sales-opportunity.card.company-name-${id}`}
        >
          {company?.name}
        </Typography>
        <SalesOpportunityStatusChip status={status} />
      </Box>

      {/* TODO: render customer address once BE exposes `billingLocation` relation on SalesPipeline (currently only `billingLocationId` is available). */}

      {product && (
        <Box className='flex items-center gap-x-2' data-testid={`sales-opportunity.card.product-${id}`}>
          <StatusIndicator
            status={product.computedProductStatus ?? ComputedProductStatus.Inactive}
            map={ComputedProductStatusMap}
          />
          <Typography
            className='text-sm leading-tight text-gray-800'
            data-testid={`sales-opportunity.card.product-public-id-${id}`}
          >
            {product.publicId}
          </Typography>
          <DisplayAS400Id id={product.externalSystemId} data-testid={`sales-opportunity.card.product-as400-${id}`} />
        </Box>
      )}

      {product?.companyProductDescription && (
        <Typography
          className='text-sm leading-tight text-gray-700'
          data-testid={`sales-opportunity.card.product-description-${id}`}
        >
          {product.companyProductDescription}
        </Typography>
      )}

      <Box className='flex items-center justify-between'>
        <Typography className='text-xs font-medium leading-tight text-gray-600'>Quote Sent:</Typography>
        {quoteDate ? (
          <DateChip date={quoteDate} data-testid={`sales-opportunity.card.quote-sent-date-${id}`} />
        ) : (
          <CloseOutlined className='text-gray-500' data-testid={`sales-opportunity.card.quote-sent-empty-${id}`} />
        )}
      </Box>
    </Paper>
  )
}
